<template>
  <SfInput v-bind="restOfProps" v-model="data" :error-text="effectiveErrors">
    <template #right-element>
      <div v-if="limit > 0" data-testid="sf-input-extended-character-count">
        {{ remainingCharactersText }}
      </div>
    </template>
  </SfInput>
</template>
<script setup lang="ts">
import { computed, toRef, watch } from 'vue'

import SfInput from './sf-input.vue'
import useError from './use-error'
import useModel from '@app/shared/vue/composables/use-model'

defineOptions({
  name: 'SfInputExtended',
})

interface Props {
  align?: 'left' | 'center' | 'right'
  componentType?: 'input' | 'textarea'
  focused?: boolean | number
  disabled?: boolean
  errorText?: string | string[]
  hasError?: boolean
  helperText?: string
  hideBrowserAppearance?: boolean
  label?: string
  limit?: number
  limitTranslation?: string
  modelValue?: string
  placeholder?: string
  required?: boolean
  noClip?: boolean
  small?: boolean
  hasFloatingLabel?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  align: 'left',
  componentType: 'input',
  focused: 0,
  errorText: () => [],
  helperText: '',
  label: '',
  limit: 0,
  limitTranslation: '',
  placeholder: '',
  modelValue: '',
  hasFloatingLabel: false,
})

const emit =
  defineEmits<{
    'update:modelValue': [value: string | undefined]
  }>()

const data = useModel(props, emit)

const errors = useError(toRef(props, 'errorText'))

const effectiveErrors = computed(() => {
  if (!isValueOverLimit.value) {
    return errors.value
  }

  return [
    ...errors.value,
    window.I18n.t(props.limitTranslation, { length: props.limit }),
  ]
})

const isValueOverLimit = computed(() => {
  return props.limit && remainingCharacters.value < 0
})

const remainingCharacters = computed(() => {
  return props.limit - valueLength.value
})

const remainingCharactersText = computed(() => {
  return `${valueLength.value}/${props.limit}`
})

const restOfProps = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { limit, limitTranslation, noClip, ...rest } = props

  return rest
})

const valueLength = computed(() => {
  return data.value?.length || 0
})

watch(data, (newValue, oldValue) => {
  if (
    !props.noClip &&
    newValue !== oldValue &&
    props.limit &&
    newValue &&
    newValue.length > props.limit
  ) {
    const effectiveValue = newValue.substring(0, props.limit)

    emit('update:modelValue', effectiveValue)
  }
})
</script>
